import { Check, Copy } from "lucide-react";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button.tsx";
import { errorToast } from "@/components/ui/toast.tsx";
import { resolveApiPath } from "@/lib/utils.ts";
import { buildMcpCommands } from "@/shared/mcp.ts";
import { SettingsPanelHeader } from "./SettingsPanelHeader.tsx";

/**
 * The MCP endpoint users paste into Claude or Codex. Always fully qualified —
 * it is copied out of the app and used by a client that has no page origin to
 * resolve a relative path against.
 */
export function buildMcpEndpointUrl(): string {
  return resolveApiPath("/api/p/mcp");
}

type CommandConfig = {
  id: "endpoint" | "claude" | "codex";
  label: string;
  value: string;
  statsigName: string;
};

const CopyButton = ({
  item,
  copiedId,
  onCopy,
}: {
  item: CommandConfig;
  copiedId: CommandConfig["id"] | null;
  onCopy: (item: CommandConfig) => void;
}) => {
  const copied = copiedId === item.id;

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={() => onCopy(item)}
      data-statsig-name={item.statsigName}
      aria-label={`Copy ${item.label}`}
      className="shrink-0"
    >
      {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
      {copied ? "Copied" : "Copy"}
    </Button>
  );
};

const CopyableBlock = ({
  item,
  copiedId,
  onCopy,
}: {
  item: CommandConfig;
  copiedId: CommandConfig["id"] | null;
  onCopy: (item: CommandConfig) => void;
}) => (
  <div className="rounded-md border bg-card p-4">
    <div className="flex items-center justify-between gap-3">
      <div className="text-sm font-medium">{item.label}</div>
      <CopyButton item={item} copiedId={copiedId} onCopy={onCopy} />
    </div>
    <pre className="mt-3 overflow-x-auto rounded-md bg-muted px-3 py-2 text-xs">
      <code>{item.value}</code>
    </pre>
  </div>
);

export const McpSetup = () => {
  const [copiedId, setCopiedId] = useState<CommandConfig["id"] | null>(null);
  const mcpUrl = useMemo(() => buildMcpEndpointUrl(), []);
  // Labels and command strings come from shared/client so native's MCP screen
  // offers byte-identical snippets; only the statsig names are web's own.
  const commands = useMemo<CommandConfig[]>(
    () =>
      buildMcpCommands(mcpUrl).map((command) => ({
        ...command,
        statsigName: `settings-mcp-copy-${command.id}`,
      })),
    [mcpUrl],
  );

  const handleCopy = (item: CommandConfig) => {
    if (!navigator.clipboard?.writeText) {
      errorToast("Couldn't copy to clipboard.");
      return;
    }

    void navigator.clipboard
      .writeText(item.value)
      .then(() => {
        setCopiedId(item.id);
        window.setTimeout(() => setCopiedId(null), 1500);
      })
      .catch(() => errorToast("Couldn't copy to clipboard."));
  };

  return (
    <div className="w-full space-y-8 pb-12">
      <div>
        <SettingsPanelHeader title="MCP" />
        <p className="text-sm text-muted-foreground">
          Connect Okthx to Claude, Codex, or another Streamable HTTP MCP client so those apps can
          ask your Okthx assistant for help.
        </p>
      </div>

      <div className="space-y-3">
        <h3 className="text-sm font-medium">What this unlocks</h3>
        <ul className="list-disc space-y-2 pl-5 text-sm text-muted-foreground">
          <li>
            Use <span className="font-medium text-foreground">okthx_chat</span> for your normal
            assistant requests from an MCP client.
          </li>
          <li>
            Use <span className="font-medium text-foreground">okthx_explain</span> to see what your
            connected Okthx assistant can do.
          </li>
          <li>
            Keep the same Okthx account, enabled capabilities, and approval gates you use in the
            app.
          </li>
        </ul>
      </div>

      <div className="space-y-3">
        <h3 className="text-sm font-medium">Add Okthx to your MCP client</h3>
        <p className="text-sm text-muted-foreground">
          Your client will open a browser consent flow before it can reach your Okthx assistant.
        </p>
        <div className="space-y-3">
          {commands.map((item) => (
            <CopyableBlock key={item.id} item={item} copiedId={copiedId} onCopy={handleCopy} />
          ))}
        </div>
      </div>
    </div>
  );
};
